import React from "react";
import { toast } from "react-toastify";
import { Input, Select, Switch } from "antd";
import moment from "moment-timezone";
import {
  getPesticidesList,
  getPesticideSampleDetails,
  getInstruments,
} from "../../../../../actions/chemistry";
import Legend from "../../../../Common/Legend/Legend";
import ChemPesticidesTable from "./ChemPesticidesTable";
import ChemPesticidesModal from "./ChemPesticidesModal";
import "./ChemPesticides.css";
import { TEST_OVERDUE_FLAGS, convertStatusMapToArrayOptions } from "../../../../../utils/statusConfig";

const MRL_INSTRUMENT_IDS = {
  GC_MS: 4,
  LC_MS: 5,
};

/**
 * Compound-level instrument is the source of truth for this feature.
 * Do not fall back to sample-level instrument_ids / instruments_list.
 */
const getCompoundInstrumentId = (compoundValue) => {
  if (!compoundValue) {
    return undefined;
  }

  if (compoundValue.pesticide_instrument_id != null) {
    return compoundValue.pesticide_instrument_id;
  }

  return compoundValue.instrument_id;
};

/**
 * Look up the instrument display name from instrumentList by id.
 */
const getInstrumentNameById = (instrumentList, instrumentId) => {
  const matchedInstrument = instrumentList.find((instrument) => instrument.id === instrumentId);
  return matchedInstrument ? matchedInstrument.instrument_name : "";
};

/**
 * Get the display name for a compound's instrument.
 * If the compound has an instrument_name, use that.
 * Otherwise, look up the instrument_id in the instrumentList.
 */
const getCompoundInstrumentDisplayName = (compoundValue, instrumentList) => {
  if (!compoundValue) {
    return "";
  }

  if (compoundValue.instrument_name) {
    return compoundValue.instrument_name;
  }

  const instrumentId = getCompoundInstrumentId(compoundValue);
  if (instrumentId == null) {
    return "";
  }

  return getInstrumentNameById(instrumentList, instrumentId);
};

const LegendContentArray = convertStatusMapToArrayOptions(TEST_OVERDUE_FLAGS, ["Retest"], true, false);

class ChemPesticides extends React.Component {
  constructor(props) {
    super(props);
    const { pesticideSampleInfo } = this.props;
    this.state = {
      loading: true,
      samples: [],
      sampleDetail: null,
      status: props.batchReportView ? "Ready-P" : "Pending",
      search: "",
      statusList: [
        { value: "Ready" },
        { value: "Ready-P" },
      ],
      apiController: null,
      showModal: false,
      compoundTableColumns: [],
      compoundTableData: [],
      instrumentList: [],
      pesticideSampleInfo,
      retest: false,
    };
    this.cmptMounted = true;
  }

  componentDidMount() {
    this.getPesticideTableData();
  }

  componentWillUnmount() {
    this.cmptMounted = false;
    this.abortPrevApiCall();
  }

  abortPrevApiCall = (controller) => {
    const { apiController } = this.state;
    if (apiController) {
      apiController.abort();
    }
    this.setState({ apiController: controller });
  };

  /**
   * Make an API call to fetch the latest pesticide pending data
   */
  getPesticideTableData = async () => {
    const { status, search, retest } = this.state;
    const payload = {
      status,
      search,
      retest,
    };
    /** Abort the previous GET call */
    const controller = new AbortController();
    this.abortPrevApiCall(controller);
    /** Make the API call */
    const {
      success, message, result, canceled,
    } = await getPesticidesList(payload, controller.signal);
    if (!this.cmptMounted) return;
    if (success) {
      this.setState({
        loading: false,
        samples: result.map((item, idx) => ({ key: idx, ...item })),
      });
    } else if (!canceled) {
      this.setState({
        loading: false,
        samples: [],
      });
      toast.error(message);
    }
  }

  handleSearchEnter = (e) => {
    const value = typeof e === "string" ? e : e.target.value;
    this.setState({ search: value, loading: true }, () => {
      this.getPesticideTableData();
    });
  }

  handleOnSearchChange = (e) => {
    if (e.target.value === "") {
      this.setState({ search: e.target.value, loading: true }, () => {
        this.getPesticideTableData();
      });
    } else {
      this.setState({ search: e.target.value });
    }
  }

  /**
   * Create a list of column object for the popup table
   */
  createCompoundColumns = (sampleDetail) => {
    const { values, sample_status } = sampleDetail;
    const columns = [
      {
        title: "Compound",
        dataIndex: "compound",
        key: "compound",
        width: "300px",
        fixed: "left",
      },
      {
        title: "Result",
        dataIndex: "compound_result",
        key: "compound_result",
        width: "140px",
      },
      {
        title: "Instrument",
        dataIndex: "instrument_id",
        key: "instrument_id",
        width: "160px",
      },
    ];

    /** Add countries as a column for "Ready-P" sample */
    if (sample_status === "Ready-P" && values.length > 0) {
      let hasUsa = false;
      values[0].data.forEach((mrlData) => {
        const { country } = mrlData;
        if (country === "USA") {
          hasUsa = true;
        } else if (country && country !== "instrument_id") {
          columns.push({
            title: country,
            dataIndex: country,
            key: country,
            width: "140px",
          });
        }
      });
      if (hasUsa) {
        columns.splice(3, 0, {
          title: "USA",
          dataIndex: "USA",
          key: "USA",
          width: "140px",
        });
      }
    }

    columns.push({
      title: <div className="ChemPesticidesTable__Action">Action</div>,
      dataIndex: "compound_row_action",
      key: "compound_row_action",
      width: "120px",
      className: "ChemPesticidesTable__Action",
      fixed: "right",
    });

    return columns;
  }

  isPesticideOrFungicide = (testName) => ["pesticide", "fungicide"].filter((value) => testName.toLowerCase().includes(value)).length > 0;

  isRowEditable = (compound) => ["all tested pesticide", "other tested pesticide", "all tested fungicide"].filter((value) => compound.toLowerCase().includes(value)).length === 0;

  getDefaultCompoundTest = (testName, compoundTableData) => {
    if (compoundTableData.length === 0) return null;
    const testLowerCase = testName.toLowerCase();
    if (testLowerCase.includes("pesticide") || testLowerCase.includes("fungicide")) {
      const targetName = testLowerCase.includes("pesticide") ? "Pesticides" : "Fungicides";
      return {
        compound: `All Tested ${targetName} Below LOQ*`,
        compound_result: "<LOQ",
      };
    }
    return null;
  }

  /**
   * Create the popup table data source based on the sampleDetail information
   */
  createCompoundTableData = (sampleDetail, instrumentList = []) => {
    const { values, sample_status } = sampleDetail;
    const tableData = [];
    values.forEach((item, idx) => {
      const instrumentId = getCompoundInstrumentId(item);
      const rowData = {
        key: idx,
        rowIndex: idx,
        compound: item.compound,
        compound_result: item.value,
        instrument_id: instrumentId,
        instrument_name: getCompoundInstrumentDisplayName(item, instrumentList)
          || getInstrumentNameById(instrumentList, instrumentId),
        editable: this.isRowEditable(item.compound),
      };
      /** Add country mrl value for "Ready-P" sample */
      if (sample_status === "Ready-P") {
        item.data.forEach((mrlData) => {
          if (mrlData.country && mrlData.country !== "instrument_id") {
            rowData[mrlData.country] = mrlData.mrl_value ?? "";
          }
        });
      }

      tableData.push(rowData);
    });
    return tableData;
  }

  fetchInstrumentList = async () => {
    const { success, result, message } = await getInstruments();
    if (!success) {
      toast.error(message);
      return null;
    }
    return result.filter(
      (instrument) => instrument.id === MRL_INSTRUMENT_IDS.GC_MS
        || instrument.id === MRL_INSTRUMENT_IDS.LC_MS,
    );
  }

  /**
   * Make an API call to fetch the sample detail and open the popup
   */
  openModal = (sample) => {
    this.setState({ loading: true }, async () => {
      const {
        workorder_id,
        genista_id,
        customer_id,
        sample_id,
        tests_in_sample_id,
        status,
      } = sample;

      const payload = {
        workorder_id,
        genista_id,
        customer_id,
        sample_id,
        tests_in_sample_id,
        status,
      };
      const instrumentList = await this.fetchInstrumentList();
      if (!instrumentList) {
        this.setState({ loading: false });
        return;
      }
      const { success, result, message } = await getPesticideSampleDetails(payload);
      if (success) {
        // Treat missing has_mrl_instrument_results / available_instruments as no instrument data.
        const compoundTableColumns = this.createCompoundColumns(result);
        const compoundTableData = this.createCompoundTableData(result, instrumentList);
        this.setState({
          showModal: true,
          sampleDetail: {
            ...result,
            tests_in_sample_id: result.tests_in_sample_id ?? tests_in_sample_id,
            report_date: moment().tz("America/Los_Angeles").format("MM/DD/YYYY"),
            received_date: moment().tz("America/Los_Angeles").format("MM/DD/YYYY"),
            showDefaultCompound: compoundTableData.length > 0 && this.isPesticideOrFungicide(result.test_name),
            defaultCompoundData: this.getDefaultCompoundTest(result.test_name, compoundTableData),
          },
          compoundTableColumns,
          compoundTableData,
          instrumentList,
        });
      } else {
        toast.error(message);
      }

      this.setState({ loading: false });
    });
  }

  closeModal = (refreshList = false) => {
    this.setState({
      showModal: false,
      loading: refreshList,
    }, () => {
      if (refreshList) {
        this.getPesticideTableData();
      }
    });
  }

  handleOnStatusChange = (status) => {
    const { pesticideSampleInfo } = this.props;
    const newSampleInfo = status === "Ready-P" ? pesticideSampleInfo : pesticideSampleInfo.filter((item) => !item.key.includes("date"));
    this.setState({ status, pesticideSampleInfo: newSampleInfo, loading: true }, () => {
      this.getPesticideTableData();
    });
  }

  handleRetestToggle = (retest) => {
    this.setState({ retest, loading: true }, () => {
      this.getPesticideTableData();
    });
  }

  render() {
    const {
      loading,
      samples,
      showModal,
      sampleDetail,
      compoundTableColumns,
      compoundTableData,
      statusList,
      status,
      instrumentList,
      pesticideSampleInfo,
      retest,
    } = this.state;
    const { tableColumns, batchReportView } = this.props;
    return (
      <div>
        <div className={`ChemPesticides__TopBar${batchReportView ? "--dynamic-height" : ""}`}>
          <div className="ChemPesticides__TopBar__FilterSection">
            <Input.Search
              className="ChemPesticides__Search"
              placeholder={`Search by Genista ID${batchReportView ? ", Customer ID" : ""}`}
              onPressEnter={(e) => this.handleSearchEnter(e)}
              onChange={(e) => this.handleOnSearchChange(e)}
              onSearch={(value) => this.handleSearchEnter(value)}
            />
            {batchReportView && (
            <div className="ChemPesticides__DropdownContainer">
              <div className="PesticidesFilterLabel">
                Status
              </div>
              <Select
                className="ChemPesticides__ActionSelect"
                popupClassName="ChemPesticides__ActionSelect__Menu"
                defaultValue={status}
                options={statusList}
                onSelect={(newStatus) => this.handleOnStatusChange(newStatus)}
              />
            </div>
            )}
            <div className="ChemPesticides__DropdownContainer">
              <div className="PesticidesFilterLabel">
                Retest
              </div>
              <Switch value={retest} onChange={this.handleRetestToggle} />
            </div>
          </div>
          <Legend legendContent={LegendContentArray} />
        </div>
        <ChemPesticidesTable
          batchReportView={batchReportView}
          tableColumns={tableColumns}
          samples={samples}
          loading={loading}
          openModal={(sample) => this.openModal(sample)}
        />
        {showModal && (
        <ChemPesticidesModal
          readyP={status === "Ready-P"}
          showSubmit={status === "Ready-P" || status === "Pending"}
          tableColumns={compoundTableColumns}
          compoundTableData={compoundTableData}
          loading={loading}
          parentSampleDetail={sampleDetail}
          pesticideSampleInfo={pesticideSampleInfo}
          instrumentList={instrumentList}
          closeModal={(refresh) => this.closeModal(refresh)}
        />
        )}
      </div>
    );
  }
}

export default ChemPesticides;
